import React, { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LangContext from 'context/LangContext'
import ComponentsUNames from 'constants/componentsUNames'
import LocalStorage from 'utils/myLocalStorage'
import { DisplayModus, WORKORDER_TYPES } from 'constants/constants'
import PageHeader from 'components/pageHeader'
import { Chip, Container, IconButton, Inline } from '@airbus/components-react/components'
import { approStore, execStore, Workorder } from 'types/workorder/workorder'
import { NavItem } from 'types/navItems'
import WoDetails from './components/WoDetails/woDetails'
import { faAnglesDown, faAnglesUp, faGear, faPencil } from '@fortawesome/free-solid-svg-icons'
import WoRequiredTools from './components/RequiredTools/woRequiredTools'
import WoUsedTools from './components/UsedTools/woUsedTools'
import WoManhour from './components/Manhour/woManhour'
import WoLinkedDocs from './components/LinkedDocs/woLinkedDocs'
import WoInstallationsRemovals from './components/InstallationsRemovals/woInstallationsRemovals'
import { RequiredToolInput } from 'types/workorder/requiredTool'
import { AircraftDetail, TechlogDetail } from 'types/techlog'
import useQuery from 'utils/hooks/useQuery'
import urlApis from 'utils/api/urlApis'
import { parse } from 'date-fns'
import myLocalStorage from 'utils/myLocalStorage'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const INCIDENT_NUMBER_FIRST = '001'

const ActionButtons = ({
  displayMode,
  setDisplayMode,
  isAllExpanded,
  setIsAllExpanded,
  statusAbv,
  oeType,
  isLoading = false,
}: {
  displayMode: DisplayModus
  setDisplayMode: (b: DisplayModus) => void
  isAllExpanded: boolean
  setIsAllExpanded: (b: boolean) => void
  statusAbv: string | undefined
  oeType: string | undefined
  isLoading: boolean
}) => {
  const { t } = useTranslation()

  // Update permissions allow both edition and execution
  const canUpdate =
    LocalStorage.getOrDefault(
      [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_UPDATE_PERMISSIONS],
      LocalStorage.VALUE_TYPE.BOOLEAN,
      false,
    ) &&
    (oeType !== 'DOC' ||
      (oeType === 'DOC' &&
        LocalStorage.getOrDefault(
          [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_UPDATE_OE_SB_PERMISSIONS],
          LocalStorage.VALUE_TYPE.BOOLEAN,
          false,
        )))

  const isEditMode = [DisplayModus.WRITE, DisplayModus.EXEC, DisplayModus.CREATE].includes(displayMode)

  return (
    <Inline>
      {!isEditMode && canUpdate && statusAbv !== 'CLO' && (
        <IconButton
          variant='ghost'
          size='xsmall'
          onClick={() => {
            setDisplayMode(DisplayModus.WRITE)
          }}
          title={t('7000398')}
          disabled={isLoading}
        >
          <FontAwesomeIcon icon={faPencil} />
        </IconButton>
      )}
      {!isEditMode && canUpdate && statusAbv !== 'CLO' && (
        <IconButton
          variant='ghost'
          size='xsmall'
          onClick={() => {
            setDisplayMode(DisplayModus.EXEC)
          }}
          title={t('execute')}
          disabled={isLoading}
        >
          <FontAwesomeIcon icon={faGear} />
        </IconButton>
      )}
      <IconButton
        variant='ghost'
        size='xsmall'
        onClick={() => setIsAllExpanded(!isAllExpanded)}
        title={isAllExpanded ? t('collapse_all') : t('expand_all')}
      >
        <FontAwesomeIcon icon={isAllExpanded ? faAnglesUp : faAnglesDown} />
      </IconButton>
    </Inline>
  )
}

const WorkorderPage = () => {
  const baseUrl = process.env.PUBLIC_URL
  const { t } = useTranslation()
  const { lang } = useContext(LangContext)
  const { avimmatr = null, rmdate = null, rmdoc = null, workorder = null } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const [woInit, setWoInit] = useState<Workorder>()
  const [usedTools, setUsedTools] = useState([])

  const { fetchData: fetchAircraft } = useQuery<AircraftDetail>(urlApis.URL_CRM_AC_GET)
  const { fetchData: fetchParentTechlog } = useQuery<TechlogDetail>(urlApis.URL_CRM_BYFLIGHT_GET)
  const { fetchData: fetchWorkorders } = useQuery<Workorder[]>(urlApis.URL_CRM_WORKORDERS_GET_PARAMETERIZED)

  const [selectedResource, setSelectedResource] = useState<RequiredToolInput>()
  const [keyManHour, setKeyManHour] = useState(0)

  const [isAllExpanded, setIsAllExpanded] = useState<boolean>(false)
  const [statusAbv, setStatusAbv] = useState<string>()
  const [oeType, setOeType] = useState<string>()

  let navItems: NavItem[] = []
  if (location.pathname.includes('/techlogs/') && avimmatr && rmdate && rmdoc) {
    // Routes from Techlog
    navItems = [
      {
        title: t('7001825'),
        url: baseUrl + '/techlogs',
      },
      {
        title: avimmatr as string,
        url: baseUrl + '/techlogs?search=' + avimmatr + (rmdoc && rmdoc !== '' ? '&id=' + rmdoc : ''),
      },
      { title: rmdate + ' / ' + rmdoc, url: baseUrl + '/techlogs/' + avimmatr + '/' + rmdate + '/' + rmdoc },
      {
        title: workorder ? workorder : '',
        url: baseUrl + '/techlogs/' + avimmatr + '/' + rmdate + '/' + rmdoc + '/' + workorder,
      },
    ]
  } else if (location.pathname.includes('/fleetpan/')) {
    // Routes from fleetpan
    if (avimmatr && workorder && !rmdate && !rmdoc) {
      // Display WO from fleetpan
      navItems = [
        {
          title: t('9500050'),
          url: baseUrl + '/fleetpan',
        },
        { title: avimmatr, url: baseUrl + '/fleetpan/' + avimmatr },
        { title: avimmatr + ' - ' + workorder, url: baseUrl + '/fleetpan/' + avimmatr + '/' + workorder },
      ]
    } else if (avimmatr && rmdate && rmdoc) {
      // Display WO | New WO from a TL coming from fleetpan
      navItems = [
        {
          title: t('9500050'),
          url: baseUrl + '/fleetpan',
        },
        { title: avimmatr, url: baseUrl + '/fleetpan?search=' + avimmatr },
        { title: rmdate + ' / ' + rmdoc, url: baseUrl + '/fleetpan/' + avimmatr + '/' + rmdate + '/' + rmdoc },
        {
          title: workorder ? workorder : '',
          url: baseUrl + '/fleetpan/' + avimmatr + '/' + rmdate + '/' + rmdoc + '/' + workorder,
        },
      ]
    }
  } else if (!avimmatr && workorder && !rmdate && !rmdoc) {
    // Route from workpackage
    navItems = [
      {
        title: t('9500092'),
        url: baseUrl + '/workpackage',
      },
      { title: workorder, url: baseUrl + '/workpackage/wo/' + workorder },
    ]
  }

  const [displayMode, setDisplayMode] = useState(workorder ? DisplayModus.READ : DisplayModus.CREATE)
  const [applicability, setApplicability] = useState<string>('')
  const [approStore, setApproStore] = useState<approStore>()
  const [execStore, setExecStore] = useState<execStore>()

  const nextIncidentNumber = (workorders: Workorder[]) => {
    if (workorders && workorders.length > 0) {
      const maxNumber =
        (Math.max(...workorders.map((o: Workorder) => o.reference?.incidentNumber as unknown as number)) || 0) + 1
      return ('00000000' + maxNumber).slice(-workorders[0].reference.incidentNumber.length)
    }
    return INCIDENT_NUMBER_FIRST
  }

  /**
   * Data from parent Techlog are used when creating a new Workorder in order to initialise it.
   */
  const loadParentTechlog = async () => {
    // YYY-MM-DD format
    const rmdateTransformed =
      rmdate && rmdate.substring(0, 4) + '-' + rmdate.substring(4, 6) + '-' + rmdate.substring(6)

    // Load aircraft details to get avnumap of aircraft
    const aircraftResponse = await fetchAircraft({
      lang,
      avimmatr: avimmatr,
    })
    const avnumap = aircraftResponse?.data[0]?.avnumap

    // Load techlog details in order to get flight details for initialising the Workorder
    const techlogResponse = await fetchParentTechlog({
      lang: lang,
      avnumap: avnumap,
      rmdate: rmdateTransformed,
      rmdoc: rmdoc,
    })
    const applicability = techlogResponse?.data?.appl
    const flightDetail = techlogResponse?.data?.flightDetail[0]

    // Load workorders in order to compute next incident number
    const flightNumber = ('0' + flightDetail?.voord).slice(-2)
    const workordersResponse = await fetchWorkorders({
      lang: lang,
      avnumap: flightDetail?.avnumap || null,
      rmdate: rmdateTransformed,
      rmdoc: rmdoc,
      voord: flightNumber,
    })
    const workorders = workordersResponse?.data

    const openingDate = flightDetail?.rmdate
      ? ('0' + flightDetail?.rmdate[2]).slice(-2) +
        '/' +
        ('0' + flightDetail?.rmdate[1]).slice(-2) +
        '/' +
        flightDetail?.rmdate[0]
      : null
    setWoInit({
      aircraft: flightDetail?.avnumap as string,
      aircraftNumber: flightDetail?.avnumap as string,
      avimmatr: flightDetail?.avimmatr as string,
      aircraftRegistration: flightDetail?.avimmatr as string,
      appl: applicability,
      no: undefined,
      openingDate: openingDate && parse(openingDate, 'P', new Date()),
      type: {
        type: WORKORDER_TYPES['TL'],
        subType: '',
        code: 'TL',
        label: '',
      },
      reference: {
        techlogNumber: flightDetail?.rmdoc as string,
        flightNumber: '01',
        incidentNumber: nextIncidentNumber(workorders),
      },
      formattedReference: (flightDetail?.rmdoc as string)?.replace(/\s+/g, '  '),
      issuer: {
        companyCode: myLocalStorage.getOrDefault(
          [myLocalStorage.KEY_USER_COMPANY],
          myLocalStorage.VALUE_TYPE.STRING,
          undefined,
        ),
        storeCode: myLocalStorage.getOrDefault(
          [myLocalStorage.KEY_USER_STORE],
          myLocalStorage.VALUE_TYPE.STRING,
          undefined,
        ),
      },
      execLocation: {
        companyCode: myLocalStorage.getOrDefault(
          [myLocalStorage.KEY_USER_COMPANY],
          myLocalStorage.VALUE_TYPE.STRING,
          undefined,
        ),
        storeCode: myLocalStorage.getOrDefault(
          [myLocalStorage.KEY_USER_STORE],
          myLocalStorage.VALUE_TYPE.STRING,
          undefined,
        ),
        serviceCode: myLocalStorage.getOrDefault(
          [myLocalStorage.KEY_USER_SERVICE],
          myLocalStorage.VALUE_TYPE.STRING,
          undefined,
        ),
      },
    })
  }

  /**
   *Once an action has been performed succesfully on a WO, reload the WO to see latest updates
   * @param {*} woNo Work-order number on which an action has been performed successfully (creation, status update, etc.)
   */
  const handleSubmit = (modeDisplay: DisplayModus, oenumdoc: string) => {
    if (modeDisplay === DisplayModus.CREATE) {
      const urlToNavigate = location.pathname.replace('/new', '')
      navigate(urlToNavigate)
    } else {
      setDisplayMode(DisplayModus.READ)
      navigate(0)
    }
  }

  /**
   * Once the WO's details are loaded, pass some data to component Required Tools & Man-Hour
   * @param {*}
   */
  const handleDetailsLoaded = (
    statusAbv: string,
    oeType: string,
    appllicability: string,
    woApproStore: approStore,
    woExecStore: execStore,
  ) => {
    setStatusAbv(statusAbv)
    setOeType(oeType)
    setApplicability(appllicability)
    setApproStore(woApproStore)
    setExecStore(woExecStore)
  }

  const handleReloadManHour = () => setKeyManHour(keyManHour + 1)

  useEffect(() => {
    if (displayMode === DisplayModus.CREATE) {
      loadParentTechlog()
    }
  }, [lang, displayMode])

  return (
    <>
      <PageHeader
        pageTitle={
          <Inline spacing='1-x'>
            <span>{t('9500144')}</span>
            {avimmatr && <Chip label={avimmatr} />}
            {workorder && <Chip label={workorder} />}
          </Inline>
        }
        navItems={navItems}
        ActionButtons={({ isLoading }) => (
          <ActionButtons
            isLoading={isLoading}
            displayMode={displayMode}
            setDisplayMode={setDisplayMode}
            isAllExpanded={isAllExpanded}
            setIsAllExpanded={setIsAllExpanded}
            statusAbv={statusAbv}
            oeType={oeType}
          />
        )}
      />
      <Container until='lg' style={{ flex: 1, padding: '0 3rem' }}>
        <WoDetails
          lang={lang}
          oenumdoc={workorder}
          woInit={woInit}
          displayMode={displayMode}
          setDisplayMode={setDisplayMode}
          onSubmitWorkOrder={handleSubmit}
          onDetailsLoaded={handleDetailsLoaded}
          onReloadManHour={handleReloadManHour}
        />
        {displayMode !== DisplayModus.CREATE && (
          <>
            <WoRequiredTools
              lang={lang}
              oenumdoc={workorder}
              oeType={oeType}
              selectedResource={selectedResource}
              setSelectedResource={setSelectedResource}
              approStore={approStore}
              execStore={execStore}
              displayMode={displayMode}
              expandAll={isAllExpanded}
              usedTools={usedTools}
            />
            <WoUsedTools
              lang={lang}
              oenumdoc={workorder}
              oeType={oeType}
              selectedResource={selectedResource}
              displayMode={displayMode}
              usedTools={usedTools}
              setUsedTools={setUsedTools}
              expandAll={isAllExpanded}
            />
            {LocalStorage.getOrDefault(
              [LocalStorage.KEY_PERMISSIONS, 'AMSWEB_ACCESS_MOMO-DSP'],
              LocalStorage.VALUE_TYPE.BOOLEAN,
              false,
            ) && (
              <WoManhour
                keyForReload={'key_woManHour_' + keyManHour}
                lang={lang}
                oenumdoc={workorder}
                oeType={oeType}
                applicability={applicability}
                displayMode={displayMode}
                expandAll={isAllExpanded}
              />
            )}
            <WoLinkedDocs
              lang={lang}
              oenumdoc={workorder}
              oeType={oeType}
              displayMode={displayMode}
              expandAll={isAllExpanded}
            />
            <WoInstallationsRemovals
              lang={lang}
              oenumdoc={workorder}
              displayMode={displayMode}
              expandAll={isAllExpanded}
            />
          </>
        )}
      </Container>
    </>
  )
}

export default WorkorderPage
